
import React from "react";
import { useBoat, BoatType } from "@/context/BoatContext";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Anchor, Edit, Trash2, Plus, Clock } from "lucide-react";
import BoatForm from "@/components/boats/BoatForm";
import { useTranslation } from "react-i18next";

const BoatsPage = () => {
  const { t } = useTranslation("boats");
  const { boats, selectedBoat, setSelectedBoat, removeBoat } = useBoat();
  const [editBoat, setEditBoat] = React.useState<BoatType | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);

  return (
    <MainLayout>
      <div className="space-y-4 lg:space-y-6 p-4 lg:p-0">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold tracking-tight">{t("boatsPage.title")}</h1>
            <p className="text-sm lg:text-base text-muted-foreground">
              {t("boatsPage.subtitle")}
            </p>
          </div>
          <Button 
            onClick={() => setIsAddDialogOpen(true)} 
            className="flex items-center gap-2 bg-nautic-gold hover:bg-nautic-lightgold text-nautic w-full lg:w-auto"
          >
            <Plus className="h-4 w-4" />
            {t("boatsPage.addBoat")}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
          {boats.map((boat) => (
            <Card 
              key={boat.id}
              className={`cursor-pointer transition-all hover:border-blue-300 ${
                selectedBoat?.id === boat.id ? 'border-nautic-gold border-2' : ''
              }`}
              onClick={() => setSelectedBoat(boat)}
            >
              <CardHeader className="p-3 lg:p-4">
                <div className="h-28 sm:h-32 lg:h-40 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                  {boat.image ? (
                    <img 
                      src={boat.image} 
                      alt={boat.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Anchor className="h-12 sm:h-16 lg:h-20 w-12 sm:w-16 lg:w-20 text-gray-400" />
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-3 lg:p-4">
                <CardTitle className="text-base lg:text-xl mb-2 flex items-center justify-between">
                  <span className="truncate pr-2">{boat.name}</span>
                  {boat.engineHours && (
                    <div className="flex items-center text-sm font-normal text-muted-foreground flex-shrink-0">
                      <Clock className="h-3 w-3 mr-1" />
                      <span className="text-xs lg:text-sm">{boat.engineHours}h</span>
                    </div>
                  )}
                </CardTitle>
                <div className="text-xs lg:text-sm text-muted-foreground space-y-1">
                  <p className="truncate">{boat.model} ({boat.year})</p>
                  <p className="text-xs lg:text-sm">{boat.length}m x {boat.width}m x {boat.draft}m</p>
                  <p className="truncate text-xs lg:text-sm">{boat.engine}</p>
                  <div className="grid grid-cols-2 gap-1 mt-2">
                    <div className="flex flex-col">
                      <span className="text-xs font-medium">{t("boatsPage.water")}</span>
                      <span className="text-xs">{boat.waterCapacity} L</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-medium">{t("boatsPage.fuel")}</span>
                      <span className="text-xs">{boat.fuelCapacity} L</span>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col sm:flex-row justify-end gap-2 p-3 lg:p-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditBoat(boat);
                  }}
                  className="text-xs lg:text-sm w-full sm:w-auto"
                >
                  <Edit className="h-3 w-3 lg:h-4 lg:w-4 mr-1" /> {t("boatsPage.edit")}
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeBoat(boat.id);
                  }}
                  className="text-xs lg:text-sm w-full sm:w-auto"
                >
                  <Trash2 className="h-3 w-3 lg:h-4 lg:w-4 mr-1" /> {t("boatsPage.delete")}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[90vw] lg:max-w-[600px] mx-4 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("boatsPage.addDialogTitle")}</DialogTitle>
            </DialogHeader>
            <BoatForm onComplete={() => setIsAddDialogOpen(false)} />
          </DialogContent>
        </Dialog>

        <Dialog open={!!editBoat} onOpenChange={() => setEditBoat(null)}>
          <DialogContent className="sm:max-w-[90vw] lg:max-w-[600px] mx-4 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("boatsPage.editDialogTitle")}</DialogTitle>
            </DialogHeader>
            {editBoat && (
              <BoatForm boat={editBoat} onComplete={() => setEditBoat(null)} />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default BoatsPage;
